import type { ReactElement } from 'react'
import type { CanvasPosition } from '../../../../types/infrastructure-map'
import { CollapseIndicator } from '../CollapseIndicator'

export const BUS_PADDING_X = 40
export const BUS_TOLERANCE_Y = 10
export const HIT_AREA_RADIUS = 20
export const BUS_LINE_COLOR = 'rgb(100, 116, 139)'
export const CONNECTION_POINT_RADIUS = 6
export const BUS_LINE_WIDTH = 4
export const DROP_LINE_WIDTH = 2

export interface BusNode {
  endpointId: string
  position: CanvasPosition
  isHub: boolean
  childCount: number
  isCollapsed: boolean
}

interface BackboneProps {
  minX: number
  maxX: number
  busY: number
}

export function BusBackbone({ minX, maxX, busY }: BackboneProps): ReactElement {
  return (
    <line
      x1={minX}
      y1={busY}
      x2={maxX}
      y2={busY}
      stroke={BUS_LINE_COLOR}
      strokeWidth={BUS_LINE_WIDTH}
      strokeLinecap="round"
      className="dark:stroke-slate-400"
    />
  )
}

interface NodeProps {
  node: BusNode
  busY: number
  hoveredNodeId: string | null
  setHoveredNodeId: (id: string | null) => void
  onToggleCollapse: ((endpointId: string) => void) | undefined
  onToggleCollapseRecursive: ((endpointId: string) => void) | undefined
}

export function BusNodeMarker({
  node,
  busY,
  hoveredNodeId,
  setHoveredNodeId,
  onToggleCollapse,
  onToggleCollapseRecursive,
}: NodeProps): ReactElement {
  const isOnBus = Math.abs(node.position.y - busY) < BUS_TOLERANCE_Y
  const connectionX = node.position.x
  return (
    <g
      onMouseEnter={() => {
        setHoveredNodeId(node.endpointId)
      }}
      onMouseLeave={() => {
        setHoveredNodeId(null)
      }}
    >
      {!isOnBus && (
        <line
          x1={connectionX}
          y1={node.position.y}
          x2={connectionX}
          y2={busY}
          stroke={BUS_LINE_COLOR}
          strokeWidth={DROP_LINE_WIDTH}
          className="dark:stroke-slate-400"
        />
      )}
      <circle
        cx={connectionX}
        cy={busY}
        r={CONNECTION_POINT_RADIUS}
        fill={node.isHub ? 'rgb(217, 70, 239)' : BUS_LINE_COLOR}
        stroke="white"
        strokeWidth={2}
        className={node.isHub ? '' : 'dark:fill-slate-400'}
      />
      {!node.isHub && onToggleCollapse && onToggleCollapseRecursive && (
        <g style={{ pointerEvents: 'auto' }}>
          <circle
            cx={connectionX}
            cy={busY}
            r={HIT_AREA_RADIUS}
            fill="transparent"
            style={{ cursor: 'pointer' }}
          />
          <CollapseIndicator
            position={{ x: connectionX, y: busY }}
            isCollapsed={node.isCollapsed}
            childCount={1}
            isVisible={hoveredNodeId === node.endpointId}
            onClick={() => {
              onToggleCollapse(node.endpointId)
            }}
            onShiftClick={() => {
              onToggleCollapseRecursive(node.endpointId)
            }}
          />
        </g>
      )}
    </g>
  )
}

interface HubLineProps {
  hubNode: BusNode | undefined
  busY: number
}

export function HubDropLine({ hubNode, busY }: HubLineProps): ReactElement | null {
  if (!hubNode || Math.abs(hubNode.position.y - busY) < BUS_TOLERANCE_Y) return null
  return (
    <line
      x1={hubNode.position.x}
      y1={hubNode.position.y}
      x2={hubNode.position.x}
      y2={busY}
      stroke="rgb(217, 70, 239)"
      strokeWidth={DROP_LINE_WIDTH + 1}
    />
  )
}
